import { Bell } from 'lucide-react';
import { CampaignFormSectionCard } from '@/components/campaigns/campaign-form-section-card';
import { NotificationPreview } from '@/components/notification-preview';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OptionButtonGroup } from '@/components/ui/option-button-group';
import { Textarea } from '@/components/ui/textarea';
import { WithTooltip } from '@/components/with-tooltip';
import { useI18nContext } from '@/i18n/i18n-react';

export type CampaignLinkDestination = 'home' | 'custom';

export interface CampaignMessageSectionProps {
  title: string;
  body: string;
  link: string;
  linkDestination: CampaignLinkDestination;
  iconUrl?: string | null;
  storeWebsiteDomain?: string | null;
  titleError?: string;
  bodyError?: string;
  linkError?: string;
  canSendTest: boolean;
  onOpenTestDialog: () => void;
  onLinkDestinationChange: (value: CampaignLinkDestination) => void;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  className?: string;
}

export function CampaignMessageSection({
  title,
  body,
  link,
  linkDestination,
  iconUrl,
  storeWebsiteDomain,
  titleError,
  bodyError,
  linkError,
  canSendTest,
  onOpenTestDialog,
  onLinkDestinationChange,
  onChange,
  onBlur,
  className,
}: CampaignMessageSectionProps) {
  const { LL } = useI18nContext();

  const linkDestinationOptions = [
    {
      value: 'home' as const,
      label: LL.createCampaign.linkDestination.home(),
    },
    {
      value: 'custom' as const,
      label: LL.createCampaign.linkDestination.custom(),
    },
  ];

  return (
    <CampaignFormSectionCard
      icon={Bell}
      title={LL.createCampaign.sections.message.title()}
      description={LL.createCampaign.sections.message.description()}
      className={className}
      actions={
        <WithTooltip
          tooltip={LL.createCampaign.testNotificationTooltip()}
          disabled={canSendTest}
        >
          <Button
            type="button"
            onClick={onOpenTestDialog}
            disabled={!canSendTest}
          >
            {LL.createCampaign.testNotificationButton()}
          </Button>
        </WithTooltip>
      }
    >
      <div className="space-y-4">
        <NotificationPreview title={title} body={body} iconUrl={iconUrl} />

        <div className="space-y-2">
          <Label htmlFor="title">
            {LL.createCampaign.notificationTitleLabel()}
          </Label>
          <Input
            id="title"
            name="title"
            value={title}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={LL.createCampaign.notificationTitlePlaceholder()}
            required
            className={titleError ? 'border-red-500' : ''}
          />
          {titleError ? (
            <p className="mt-1 text-sm text-red-500">{titleError}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="body">
            {LL.createCampaign.notificationBodyLabel()}
          </Label>
          <Textarea
            id="body"
            name="body"
            value={body}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={LL.createCampaign.notificationBodyPlaceholder()}
            required
            className={bodyError ? 'border-red-500' : ''}
          />
          {bodyError ? (
            <p className="mt-1 text-sm text-red-500">{bodyError}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label className="block">
            {LL.createCampaign.linkDestination.label()}
          </Label>
          <OptionButtonGroup
            value={linkDestination}
            options={linkDestinationOptions}
            onChange={onLinkDestinationChange}
            ariaLabel={LL.createCampaign.linkDestination.ariaLabel()}
          />
          {linkDestination === 'home' ? (
            <p className="text-sm text-muted-foreground">
              {LL.createCampaign.linkDestination.homeDescription()}
            </p>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="link">
                {LL.createCampaign.notificationLinkLabel()}
              </Label>
              <Input
                id="link"
                name="link"
                value={link}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={LL.createCampaign.notificationLinkPlaceholder({
                  domain: storeWebsiteDomain ?? 'your-store.com',
                })}
                className={linkError ? 'border-red-500' : ''}
              />
              {linkError ? (
                <p className="mt-1 text-sm text-red-500">{linkError}</p>
              ) : null}
              <p className="text-sm text-muted-foreground">
                {LL.createCampaign.linkDestination.customDescription()}
              </p>
            </div>
          )}
        </div>
      </div>
    </CampaignFormSectionCard>
  );
}
